import React from 'react'

import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Flex, { FlexItem } from 'styled-flex-component'

import StopPropagation from '@components/UI/StopPropagation/StopPropagation'
import { EventTrackingNames, PageRoutes } from '@enums'
import { IApplicationState } from '@store'
import {
  selectBrandFanGroupDescription,
  selectBrandFanGroupMembeCountWithoutAvatars,
  selectBrandFanGroupMemberAvatars,
  selectBrandFanGroupMemberCount,
  selectBrandFanGroupName,
} from '@store/brandFans'
import { selectCreateCampaignLink } from '@store/campaigns'
import { Container } from '@tribegroup'
import BrandFansGroupRowCreateCampaignButton from './CreateCampaign/Button'
import BrandFansGroupRowActions from './Actions'
import { BrandFansGroupRowAvatarList } from './AvatarList'
import BrandFansGroupRowDescription from './Description'
import BrandFansRowHeader from './Header'
import { HideContentOnHover, Root, ShowContentOnHover } from './Row.styled'
import BrandFansGroupRowTrackedRoute from './TrackedRoute'

interface IProps {
  groupId: number
}

const BrandFansGroupRow: React.FC<IProps> = ({ groupId }) => {
  const history = useHistory()
  const groupName = useSelector((state: IApplicationState) =>
    selectBrandFanGroupName(state, groupId),
  )
  const description = useSelector(
    (state: IApplicationState) => selectBrandFanGroupDescription(state, groupId) ?? '',
  )
  const avatars = useSelector((state: IApplicationState) =>
    selectBrandFanGroupMemberAvatars(state, groupId),
  )
  const totalCount = useSelector((state: IApplicationState) =>
    selectBrandFanGroupMemberCount(state, groupId),
  )
  const identitiesWithoutAvatarCount = useSelector((state: IApplicationState) =>
    selectBrandFanGroupMembeCountWithoutAvatars(state, groupId),
  )
  const hasCreateCampaignLink = useSelector((state: IApplicationState) =>
    Boolean(selectCreateCampaignLink(state)),
  )
  const onClick = () => {
    history.push(`${PageRoutes.BrandFanGroups}/${groupId}`)
  }
  const canCreateCampaign = totalCount > 0
  return (
    <BrandFansGroupRowTrackedRoute
      hasTracking
      eventName={EventTrackingNames.BrandFansGroupDetailView}
      groupId={groupId}
    >
      <Root onClick={onClick}>
        <Flex justifyBetween alignCenter full>
          <BrandFansRowHeader groupName={groupName} totalCount={totalCount} />
          <FlexItem noShrink>
            <BrandFansGroupRowActions
              groupId={groupId}
              canCreateCampaign={canCreateCampaign}
              hideCreateCampaign={!hasCreateCampaignLink}
            />
          </FlexItem>
        </Flex>
        <Container height={3.75}>
          <Flex justifyBetween alignEnd full>
            <Container maxWidth={31.25}>
              {hasCreateCampaignLink ? (
                <>
                  <HideContentOnHover>
                    <BrandFansGroupRowDescription description={description} />
                  </HideContentOnHover>
                  <ShowContentOnHover>
                    <StopPropagation>
                      <Container noWrap>
                        <BrandFansGroupRowCreateCampaignButton
                          groupId={groupId}
                          canCreateCampaign={canCreateCampaign}
                        />
                      </Container>
                    </StopPropagation>
                  </ShowContentOnHover>
                </>
              ) : (
                <BrandFansGroupRowDescription description={description} />
              )}
            </Container>
            <FlexItem noShrink>
              <BrandFansGroupRowAvatarList
                avatars={avatars}
                identitiesWithoutAvatarCount={identitiesWithoutAvatarCount}
              />
            </FlexItem>
          </Flex>
        </Container>
      </Root>
    </BrandFansGroupRowTrackedRoute>
  )
}

export { BrandFansGroupRow }
export default BrandFansGroupRow
